/**
 * API 문서 작성 헬퍼 함수
 * 반복적인 JSX 패턴을 간소화하여 문서 작성을 용이하게 함
 */
import ParamDesc from '../../components/UI/ApiDoc/ParamDesc';
import DocDesc from '../../components/UI/ApiDoc/DocDesc';
import MatrixTable from '../../components/UI/ApiDoc/MatrixTable';

/**
 * 단일상품(std_ol_yn=Y) 등록이 가능한 쇼핑몰.
 *
 * 서버 config/shop.ts 의 SINGLE_TYPE_AVAIL_SHOPS 와 같은 목록이다.
 * 에러코드(e4040) 설명과 파라미터 설명 두 곳에서 쓰므로 여기서만 수정한다.
 * 서버 목록이 바뀌면 이 값도 함께 갱신해야 한다.
 */
export const SINGLE_TYPE_AVAIL_SHOP_NAMES = '옥션, 지마켓, 스마트스토어, 11번가, 위메프 2.0, 홈플러스, 알리바바, 알리 익스프레스, 알리 익스프레스 글로벌, 테무, 타다닥, 토스쇼핑, 쿠팡, GSSHOP';

/**
 * API 파라미터 설명 생성
 *
 * 마크업은 조립하지 않고 ParamDesc 컴포넌트에 값만 넘긴다.
 * 표현을 한곳에 모아 디자인 변경이 문서 데이터 파일에 번지지 않게 한다.
 *
 * @param {Object} params - 파라미터 객체
 * @param {string} params.title - 파라미터 제목
 * @param {string|Array<string>} params.sub - 서브 설명 (문자열 또는 배열, HTML 허용)
 * @param {any} params.defaultValue - 기본값
 * @param {any} params.maxValue - 최대값. 원문의 `Max value` 라벨에 대응한다
 * @param {Array} params.allowedValues - 허용값 배열 (문자열 배열 또는 [값, 설명] 배열)
 * @param {Array<string>} params.notes - 주의사항 배열
 * @param {{headers: Array<string>, rows: Array<Array<string>>}} params.table - 허용값 목록으로 담을 수 없는 표.
 *   헤더가 Value/Description 같은 구조 라벨이 아니라 데이터인 경우에 쓴다
 * @returns {JSX.Element|string} 설명 엘리먼트. 제목만 있으면 문자열을 그대로 반환한다
 */
export const apiParamDesc = ({ title, sub, defaultValue, maxValue, allowedValues, notes, table }) => {
  // 단순 텍스트만 있으면 엘리먼트를 만들지 않는다. TableComponent 가 문자열을 그대로 렌더한다
  if (!sub && defaultValue === undefined && maxValue === undefined && !allowedValues && !notes && !table) {
    return title;
  }

  return (
    <ParamDesc
      title={title}
      sub={sub}
      defaultValue={defaultValue}
      maxValue={maxValue}
      allowedValues={allowedValues}
      notes={notes}
      table={table}
    />
  );
};

/**
 * API 문서 상단 설명 생성 (목록형)
 *
 * 자유구조 설명(pre 코드 예시·details 접기·img 등)은 이 헬퍼로 담지 않고 JSX 를 직접 쓴다.
 * 슬롯을 늘려 흡수하려 하면 헬퍼가 HTML 빌더가 되어 문서 작성이 오히려 번거로워진다.
 *
 * @param {Object} params
 * @param {string} params.intro - 목록 앞 텍스트 (HTML 허용). 목록 없이 이것만 쓸 수 있다
 * @param {Array<string|{text: string, sub: string}>} params.items - 목록 항목. 문자열 또는 부가설명을 가진 객체
 * @param {Array<string>} params.notes - 하단 강조 박스
 * @returns {JSX.Element} 설명 엘리먼트
 */
export const apiDocDesc = ({ intro, items, notes }) => (
  <DocDesc intro={intro} items={items} notes={notes} />
);

/**
 * 호환성 매트릭스 표 생성 (쇼핑몰별 지원 여부 등)
 *
 * 헤더와 셀은 HTML 문자열을 받는다. 셀 하나에 `<code>101</code>: 사유<br/>` 처럼
 * 여러 줄이 들어가는 기존 구조를 손실 없이 옮기기 위함이다.
 * 표를 접어 두려면 호출하는 쪽에서 details/summary 로 감싼다.
 *
 * @param {Object} params
 * @param {string} params.intro - 표 앞 설명 (HTML 허용)
 * @param {Array<string>} params.headers - 헤더 셀 (HTML 허용)
 * @param {Array<Array<string>>} params.rows - 본문 행. 각 셀 HTML 허용
 * @returns {JSX.Element} 표 엘리먼트
 */
export const apiMatrixTable = ({ intro, headers, rows }) => (
  <MatrixTable intro={intro} headers={headers} rows={rows} />
);

/**
 * API 액션 테이블 생성
 * @param {string} category - 카테고리 이름 (예: '주문', '문의')
 * @param {Array<Object>} actions - 액션 객체 배열
 * @param {string} actions[].action - 액션 이름
 * @param {string} actions[].desc - 액션 설명
 * @param {string} actions[].code - 작업명 코드 (선택)
 * @param {Array<string>} actions[].notes - 주의사항 배열 (선택)
 * @returns {JSX.Element} 액션 테이블 JSX
 */
export const apiActionTable = (category, actions) => {
  return (
    <table width='700px'>
      <tbody>
        <tr>
          <th colSpan='2'>{category}</th>
        </tr>
        {actions.map((actionItem, index) => {
          const { action, desc, code, notes } = actionItem;

          return (
            <tr key={index}>
              <td width='200px'>
                <font color='red'>{action}</font>
              </td>
              <td>
                {desc}
                {code && (
                  <>
                    <br />
                    <code>{code}</code>
                  </>
                )}
                {notes && notes.length > 0 && (
                  <div className='description' style={{ padding: '12px 0' }}>
                    <ul className='style'>
                      {notes.map((note, noteIndex) => (
                        <li key={noteIndex}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
